import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mail, Phone, FileText, Users, Globe, StickyNote, ArrowDownLeft, ArrowUpRight, Plus, MessageSquare } from "lucide-react";

const CHANNELS = [
  ["email", "E-Mail", Mail], ["phone", "Telefon", Phone], ["letter", "Brief", FileText],
  ["meeting", "Termin", Users], ["portal", "Portal", Globe], ["note", "Notiz", StickyNote],
];
const CH_MAP = Object.fromEntries(CHANNELS.map(([k, l, Ic]) => [k, { l, Ic }]));

export function Correspondence({ kind, id, canWrite }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ direction: "out", channel: "email", subject: "", body: "", contact: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/claims/${kind}/${id}/correspondence`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.subject.trim() && !form.body.trim()) { toast.error("Betreff oder Text erforderlich"); return; }
    setBusy(true);
    try {
      await api.post(`/claims/${kind}/${id}/correspondence`, form);
      toast.success("Eintrag gespeichert");
      setForm({ direction: "out", channel: "email", subject: "", body: "", contact: "" });
      setOpen(false); load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="correspondence-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />Schriftverkehr</h3>
        {canWrite && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen((v) => !v)} data-testid="correspondence-add-btn">
            <Plus className="h-3 w-3 mr-1" />Eintrag
          </Button>
        )}
      </div>

      {open && (
        <div className="border border-gray-200 rounded-lg p-3 mb-3 space-y-2 bg-gray-50" data-testid="correspondence-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Richtung</Label>
              <Select value={form.direction} onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
                <SelectTrigger className="h-8" data-testid="corr-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="out">Ausgehend</SelectItem>
                  <SelectItem value="in">Eingehend</SelectItem>
                  <SelectItem value="internal">Interne Notiz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Kanal</Label>
              <Select value={form.channel} onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}>
                <SelectTrigger className="h-8" data-testid="corr-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Input className="h-8 text-sm" placeholder="Betreff" value={form.subject} data-testid="corr-subject"
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          <Input className="h-8 text-sm" placeholder="Kontakt (optional)" value={form.contact}
            onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} />
          <Textarea rows={3} className="text-sm" placeholder="Nachricht / Notiz" value={form.body} data-testid="corr-body"
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
          <div className="flex justify-end">
            <Button size="sm" className="h-8 bg-[#1B2A4A] hover:bg-[#24365d]" disabled={busy} onClick={save} data-testid="corr-save-btn">Speichern</Button>
          </div>
        </div>
      )}

      {rows.length === 0 && <p className="text-xs text-gray-400">Noch kein Schriftverkehr.</p>}
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {rows.map((r) => {
          const { l, Ic } = CH_MAP[r.channel] || CH_MAP.note;
          const incoming = r.direction === "in";
          return (
            <div key={r.id} className="border border-gray-100 rounded-lg p-2.5 text-sm" data-testid="correspondence-item">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {incoming ? <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-600" /> : <ArrowUpRight className="h-3.5 w-3.5 text-blue-600" />}
                <Ic className="h-3.5 w-3.5" />
                <span className="font-medium text-gray-700">{l}</span>
                <span>· {new Date(r.created_at).toLocaleString("de-DE")}</span>
                {r.created_by_name && <span className="ml-auto truncate max-w-[140px]">{r.created_by_name}</span>}
              </div>
              {r.subject && <p className="font-medium text-gray-800 mt-1">{r.subject}</p>}
              {r.body && <p className="text-gray-600 whitespace-pre-wrap mt-0.5">{r.body}</p>}
              {r.contact && <p className="text-xs text-gray-400 mt-0.5">Kontakt: {r.contact}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
